import { createAsyncThunk, createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';

import type { RootState } from '../store';
import { Job, JobMetrics, JobSlice, UserSettings } from '../../types/interface';
import { rtkMyUserSettings } from './individualSlice';
import { AppliedJob, LikedJob } from '../../types';
import { filterClosedJobs } from '../../utils';

const initialState: JobSlice = {
  allJobs: [],
  matchedJobs: [],
  groupedJobsByTagCount: [],
  IJM: [],
  status: 'idle',
  error: null,
  myOrgId: '',
};

// core building block of redux is state -> current redux store and actions -> the payload we are sending to our store that we want to update our state with.
const jobSlice = createSlice({
  name: 'jobs',
  initialState,
  reducers: {
    setJobsList: (state, action) => {
      return {
        ...state,
        allJobs: [
          // ...action.payload.filter((job: Job) => {
          //   return job.isJobActive ? true : false;
          // }),
          ...action.payload,
        ],
      };
    },
    fetchMatchedJobs: (state, action) => {
      //dead code, not being used
      //Initially filtered jobs based off of a certain distance, will leave this in for a breadcrumb incase this featuer wants to be used. Realistically it should/could be handled
      //in the backend, but you can use the logic below when updating websocket data for job update edge cases (address changing and being broadcasted to live users).
      action.payload.filter((job: Job) => +job.distance < 100);
      state.matchedJobs = [];
    },
    socketJobPayload: (
      state: JobSlice,
      action: PayloadAction<{ job: Job[]; metrics: JobMetrics[]; isNew: boolean; userId: string }>
    ) => {
      console.info('Incoming socket data! ', action);
      const { job: newJob, metrics, isNew, userId } = action.payload;

      //check if job exists in array, if it does, replace, if not push into array.
      if (newJob[0].isJobActive) {
        const allJobs = state.allJobs;
        const jobIndex = allJobs.findIndex((job: Job) => job.id === newJob[0].id);
        console.warn('jobIndex, ', jobIndex);

        if (metrics.length > 0) {
          //ws also sends updated metrics to job if tags are changed, so we check
          const ijm: JobMetrics | undefined = metrics.find(
            (metric: JobMetrics) =>
              metric.ijmJob === newJob[0].id && metric.ijmIndividual === userId
          );

          const jobWithMetrics = {
            ...newJob[0],
            jobCumulativeScore: ijm!.ijmTotal.toString(),
            distance: ijm!.ijmDistance.toString(),
            jobAlignmentScore: ijm!.ijmScore.toString(),
          };

          if (jobIndex === -1) {
            //cannot find, so it's a new job.
            void state.allJobs.push(jobWithMetrics);
          } else {
            state.allJobs[jobIndex] = jobWithMetrics;
            state.allJobs = [...state.allJobs];
          }
        } else {
          if (jobIndex === -1) {
            //job is re-opened and is not in list
            //leave empty for now, let user revisit page and refresh, otherwise you need to conditionally fetch metrics relative to individuals Metrics
          } else {
            const oldJob = state.allJobs[jobIndex];
            state.allJobs[jobIndex] = {
              ...newJob[0],
              jobCumulativeScore: oldJob.jobCumulativeScore,
              distance: oldJob.distance,
              jobAlignmentScore: oldJob.jobAlignmentScore,
            };
          }
        }
      } else {
        if (isNew) {
          //do nothing no need to add it to the list.
          return;
        } else {
          const newJobList = state.allJobs.filter((job: Job) => job.id !== newJob[0].id);
          state.allJobs = [...newJobList];
        }
      }
    },
    socketJobMetrics: (
      state: JobSlice,
      action: PayloadAction<{ ijMetrics: JobMetrics[]; isNew: boolean; userId: string }>
    ) => {
      const { ijMetrics, isNew, userId } = action.payload;

      updateMetrics();

      function updateMetrics() {
        const updatedJobs = state.allJobs;
        const userJobMetricsArray = ijMetrics.filter(
          (metric: JobMetrics) => metric.ijmIndividual === userId
        );

        if (userJobMetricsArray!.length > 0) {
          for (let i = 0; i < updatedJobs.length; i++) {
            const ijm: JobMetrics | undefined = userJobMetricsArray.find(
              (metric: JobMetrics) => metric.ijmJob === updatedJobs[i].id
            );

            if (ijm !== undefined) {
              updatedJobs[i] = {
                ...updatedJobs[i],
                jobCumulativeScore: ijm!.ijmTotal.toString(),
                distance: ijm!.ijmDistance.toString(),
                jobAlignmentScore: ijm!.ijmScore.toString(),
              };
            }
          }
        }

        state.allJobs = [...updatedJobs];

        // // for b2c we append metrics to the job directly.
        // let myNewMetrics = ijMetrics.filter(metric => metric.ijmIndividualOrgId === state.myOrgId);

        // if(isNew && myNewMetrics.length > 0){
        //    myNewMetrics.forEach(metric => {
        //     void state.IJM.push(metric);
        //    });
        // }else if (!isNew && myNewMetrics.length > 0){
        //   myNewMetrics.forEach(newMetric => {
        //     let index = state.IJM.findIndex(currMetric => currMetric.ijmIndividual === newMetric.ijmIndividual && currMetric.ijmJob === newMetric.ijmJob);
        //     state.IJM[index] = {...newMetric};
        //   });
        //   state.IJM = [...state.IJM]
        // }
      }
    },
    socketJobAttachmentUpdate: (
      state: JobSlice,
      action: PayloadAction<{ data: { job: Job[] } }>
    ) => {
      const updatedJob = action.payload.data.job[0];
      let index = state.allJobs.findIndex((storedJob) => storedJob.id === updatedJob.id);
      state.allJobs[index] = updatedJob;
      state.allJobs = [...state.allJobs];
    },
  },
  extraReducers: (builder) => {
    // refer to: https://redux-toolkit.js.org/api/createSlice for full details, below are some important notes.
    // If you want to do stuff 'inbetween http fetches' this is the place to do it using asyncThunkName.props  where props === fulfilled, rejected, pending.
    // below is the general template used for extraReducers, and it's impotant to note, you use extra reducers for asyncThunks ONLY. Everything else that isn't tethered to an axios request should be
    // kept int he reducers section of createSlice().
    // builder
    // .addCase(, (state, action) => {
    //   // action is inferred correctly here if using TS
    // })
  },
});

export const fetchJobs = createAsyncThunk(
  'jobs/fetchJobs',
  async (params: { individualId: string; token: string }) => {
    //currently orgId is null, when we toggle between public & private jobs, we want to pass the orgId to fetch org specific.
    console.info(params.individualId);
    return axios
      .get(`${process.env.REACT_APP_SERVER_URL}/api/b2c/job/${params.individualId}`, {
        headers: {
          Authorization: `Bearer ${params.token}`,
        },
      })
      .then((response) => {
        console.info('Response job: ', response);
        return response.data;
      })
      .catch((err) => {
        console.log(err);
      });
  }
);

export const getJobs = (state: RootState) => {
  return state.jobs.allJobs;
};

export const getIndividualJob = (state: RootState, jobId: string) => {
  return state.jobs.allJobs.find((job: Job) => job.id === jobId);
};

export const rtkAllJobs = (state: RootState) => state.jobs.allJobs;

function findMatchedJobs(myUserSettings: UserSettings | undefined, allJobs: Job[]) {
  return myUserSettings
    ? allJobs.filter((job: Job) => job && +job.jobCumulativeScore > 0 && filterClosedJobs(job))
    : [];
}

export const matchedJobsList = createSelector(
  [rtkMyUserSettings, rtkAllJobs],
  (myUserSettings, allJobs) => {
    return findMatchedJobs(myUserSettings, allJobs);
  }
);
export const allJobRelatedCounts = createSelector(
  [rtkMyUserSettings, rtkAllJobs],
  (myUserSettings, allJobs) => {
    const idList = allJobs.map((job: Job) => job.id);
    const myMatches = findMatchedJobs(myUserSettings, allJobs);
    const myLikedJobs = myUserSettings
      ? myUserSettings?.likedJobsArr!.filter((likedJob: LikedJob) => idList.includes(likedJob.id))
      : [];
    const appliedJobs = myUserSettings
      ? myUserSettings?.appliedJobsArr!.filter((appliedJob: AppliedJob) =>
          idList.includes(appliedJob.id)
        )
      : [];

    let myMatchesCount =
      myUserSettings === undefined
        ? 0
        : myUserSettings?.likedJobsArr! === null
          ? 0
          : myMatches.length;

    let allRelatedCounts = {
      liked:
        myUserSettings === undefined
          ? 0
          : myUserSettings.likedJobsArr === null
            ? 0
            : myLikedJobs!.length,

      applied:
        myUserSettings === undefined
          ? 0
          : myUserSettings.appliedJobsArr === null
            ? 0
            : appliedJobs!.length,
      matches: myMatchesCount,
    };

    return allRelatedCounts;
  }
);

export const { setJobsList, socketJobMetrics, socketJobAttachmentUpdate, socketJobPayload } =
  jobSlice.actions;
export default jobSlice.reducer;
